import { useState as useStateLocal, useCallback as useCallbackLocal } from "react";
import CCToolCallCard from "./CCToolCallCard";
import CCAgentTurnCard from "./CCAgentTurnCard";
import CCTerminalBlock from "./CCTerminalBlock";
import { AssistantTextWithPathCards, WorkspaceFileLink } from "./WorkspaceFileLink";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

// ── 执行结果块（tool_result）：默认3行，可折叠/展开 ──────────────────────────
const TR_LINE_H = 18;
const TR_COLLAPSED = 1;
const TR_DEFAULT = 3;
const TR_EXPANDED = 15;

function parseToolImage(content) {
  const fromObj = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    if (obj.type === "image" && obj.source?.data) {
      return {
        data: obj.source.data,
        mediaType: obj.source.media_type || obj.source.mediaType || "image/png",
      };
    }
    if (obj.source?.type === "base64" && obj.source?.data) {
      return {
        data: obj.source.data,
        mediaType: obj.source.media_type || "image/png",
      };
    }
    return null;
  };
  if (!content) return null;
  if (typeof content === "string") {
    const text = content.trim();
    if (text.startsWith("{") || text.startsWith("[")) {
      try {
        return parseToolImage(JSON.parse(text));
      } catch (_) {
        return null;
      }
    }
    return null;
  }
  if (Array.isArray(content)) {
    for (const item of content) {
      const hit = parseToolImage(item);
      if (hit) return hit;
    }
    return null;
  }
  return fromObj(content);
}

function CCToolResult({ msg, onOpen }) {
  const isError = msg.isError;
  const image = parseToolImage(msg.content);
  const openPath = msg.filePath || "";
  const [state, setStateLocal] = useStateLocal("default");
  const [openHint, setOpenHint] = useStateLocal("");

  const maxH = state === "collapsed"
    ? `${TR_LINE_H * TR_COLLAPSED}px`
    : state === "expanded"
      ? `${TR_LINE_H * TR_EXPANDED}px`
      : `${TR_LINE_H * TR_DEFAULT}px`;

  const handleToggle = () => {
    if (state === "collapsed") setStateLocal("default");
    else if (state === "default") setStateLocal("expanded");
    else setStateLocal("collapsed");
  };

  const btnLabel = state === "collapsed" ? "▶" : state === "expanded" ? "▲" : "▼";
  const btnTitle = state === "collapsed" ? "展开" : state === "expanded" ? "折叠" : "展开";

  return (
    <div style={{
      margin: "2px 0 4px 0",
      borderLeft: `3px solid ${isError ? "#f85149" : "#3fb950"}`,
      background: isError ? "#1a0e0e" : "#0d1a0d",
      borderRadius: "0 4px 4px 0",
      overflow: "hidden",
    }}>
      <div style={{
        padding: "2px 6px 2px 8px",
        fontSize: "10px",
        color: isError ? "#f85149" : "#3fb950",
        background: isError ? "#2d1010" : "#0f1f0f",
        display: "flex",
        alignItems: "center",
        gap: "4px",
      }}>
        <span>{isError ? "✗" : "✓"}</span>
        <span style={{ flex: 1 }}>{isError ? "执行错误" : (image ? "图片结果" : "执行结果")}</span>
        {onOpen && openPath && (
          <button
            type="button"
            onClick={async () => {
              setOpenHint("打开中…");
              try {
                const result = await onOpen(openPath);
                if (result && result.ok === false) setOpenHint(result.error || "打开失败");
                else setOpenHint("");
              } catch (e) {
                setOpenHint(e.message || "打开失败");
              }
            }}
            style={{
              background: "none",
              border: "1px solid #30363d",
              color: openHint && openHint !== "打开中…" ? "#f85149" : "#58a6ff",
              borderRadius: 3,
              fontSize: 10,
              padding: "0 6px",
              cursor: "pointer",
            }}
          >
            {openHint && openHint !== "打开中…" ? "失败" : "打开"}
          </button>
        )}
        <button
          onClick={handleToggle}
          title={btnTitle}
          style={{
            background: "none", border: "none",
            color: "#8b949e", cursor: "pointer",
            fontSize: "10px", padding: "0 2px",
            flexShrink: 0, lineHeight: 1,
          }}
        >{btnLabel}</button>
      </div>
      {image ? (
        <div style={{ padding: "8px 10px", background: "#0d1117" }}>
          <img
            alt="工具结果图片"
            src={`data:${image.mediaType};base64,${image.data}`}
            style={{ maxWidth: "100%", maxHeight: 360, borderRadius: 6, display: "block" }}
          />
        </div>
      ) : (
      <pre style={{
        margin: 0,
        padding: "6px 10px",
        fontSize: "12px",
        color: isError ? "#ffa198" : "#aff5b4",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        maxHeight: maxH,
        overflowY: state === "expanded" ? "auto" : "hidden",
        transition: "max-height 0.15s ease",
        fontFamily: "'Courier New', Consolas, monospace",
        scrollbarWidth: "thin",
        scrollbarColor: "#30363d transparent",
      }}>
        {msg.content}
      </pre>
      )}
    </div>
  );
}

// 根据文件扩展名推断语言
function guessLang(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  const MAP = {
    js: "javascript", jsx: "jsx", ts: "typescript", tsx: "tsx",
    py: "python", go: "go", java: "java", c: "c", cpp: "cpp",
    h: "c", rs: "rust", rb: "ruby", sh: "bash", bat: "batch",
    json: "json", yaml: "yaml", yml: "yaml", toml: "toml",
    md: "markdown", html: "html", css: "css", sql: "sql",
    ino: "cpp", xml: "xml",
  };
  return MAP[ext] || "text";
}

// 内联文件查看器（紧贴在触发它的消息下方）
function InlineFileViewer({ file, onClose }) {
  const lang = guessLang(file.name);
  return (
    <div style={{
      marginTop: "6px",
      border: "1px solid #30363d",
      borderRadius: "8px",
      overflow: "hidden",
      background: "#161b22",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "5px 10px",
        background: "#1c2128",
        borderBottom: "1px solid #30363d",
      }}>
        <span style={{
          color: "#58a6ff",
          fontFamily: "'Courier New', monospace",
          fontSize: "12px",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
        }}>
          📄 {file.name}
        </span>
        <span style={{ color: "#484f58", fontSize: "11px", flexShrink: 0 }}>{lang}</span>
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          title="在新标签页打开"
          style={{
            color: "#8b949e", fontSize: "11px", textDecoration: "none",
            flexShrink: 0, padding: "1px 6px",
            border: "1px solid #30363d", borderRadius: "3px",
          }}
        >↗</a>
        {file.content !== null && !file.loading && !file.error && (() => {
          const blob = new Blob([file.content], { type: "text/plain;charset=utf-8" });
          const blobUrl = URL.createObjectURL(blob);
          return (
            <a
              href={blobUrl}
              download={file.name}
              title="保存到本地"
              style={{
                color: "#3fb950", fontSize: "11px", textDecoration: "none",
                flexShrink: 0, padding: "1px 6px",
                border: "1px solid #2ea04326", borderRadius: "3px",
              }}
            >⬇ 保存</a>
          );
        })()}
        <button
          onClick={onClose}
          title="关闭预览"
          style={{
            background: "none", border: "none", color: "#6e7681",
            cursor: "pointer", fontSize: "14px", padding: "0 2px",
            lineHeight: 1, flexShrink: 0,
          }}
        >✕</button>
      </div>
      {file.loading && (
        <div style={{ padding: "14px", color: "#8b949e", fontSize: "12px", textAlign: "center" }}>
          加载中…
        </div>
      )}
      {file.error && (
        <div style={{ padding: "10px", color: "#f85149", fontSize: "12px" }}>
          ⚠ 加载失败：{file.error}
        </div>
      )}
      {!file.loading && !file.error && file.content !== null && (
        <div style={{ maxHeight: "420px", overflowY: "auto", overflowX: "auto" }}>
          <SyntaxHighlighter
            language={lang}
            style={vscDarkPlus}
            showLineNumbers
            customStyle={{
              margin: 0, borderRadius: 0, fontSize: "12px",
              background: "#0d1117", minWidth: "100%",
            }}
            lineNumberStyle={{ color: "#484f58", minWidth: "2.5em" }}
          >
            {file.content}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
}

// text 消息独立组件，自带内联文件查看器状态
function CCTextMessage({ content, onOpen }) {
  const [inlineFile, setInlineFile] = useStateLocal(null);

  const handleFileClick = useCallbackLocal(async (url, filename) => {
    // 再次点击同一文件则关闭
    if (inlineFile && inlineFile.url === url) {
      setInlineFile(null);
      return;
    }
    setInlineFile({ url, name: filename, content: null, loading: true, error: null });
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const text = await resp.text();
      setInlineFile({ url, name: filename, content: text, loading: false, error: null });
    } catch (e) {
      setInlineFile({ url, name: filename, content: null, loading: false, error: e.message });
    }
  }, [inlineFile]);

  return (
    <div style={{ padding: "4px 0" }}>
      <AssistantTextWithPathCards
        content={content}
        onOpen={onOpen}
        onFileClick={handleFileClick}
        disableMermaid={true}
      />
      {inlineFile && (
        <InlineFileViewer file={inlineFile} onClose={() => setInlineFile(null)} />
      )}
    </div>
  );
}

/**
 * 单条 CC 消息渲染（设计方案 §7.2）
 *
 * 支持的 type：
 *   user_input  - 学生自己发送的消息（右对齐气泡）
 *   text        - assistant 文本（Markdown）
 *   tool_use    - 工具调用卡片
 *   tool_result - 工具执行结果（终端输出）
 *   terminal    - system.local_command_output 终端输出
 *   result      - 任务完成/失败
 *   summary     - tool_use_summary 操作摘要
 *   suggestion  - prompt_suggestion 快捷建议
 */
const CCMessageItem = ({ msg, streamingText, onWorkspaceFileOpen, roster, onInsertPrompt }) => {
  if (!msg) return null;

  switch (msg.type) {
    case "thinking":
      return <CCThinkingBlock content={msg.content} />;

    case "agent_turn":
      return (
        <CCAgentTurnCard
          turn={msg}
          roster={roster}
          onInsertPrompt={onInsertPrompt}
          onOpenFile={onWorkspaceFileOpen}
        />
      );

    case "user_input":
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "4px 0" }}>
          <div style={{
            maxWidth: "80%",
            background: "#1f6feb",
            borderRadius: "12px 12px 2px 12px",
            padding: "8px 12px",
            fontSize: "13px",
            color: "#ffffff",
            lineHeight: 1.5,
            wordBreak: "break-word",
          }}>
            {msg.content}
          </div>
        </div>
      );

    case "text":
      return <CCTextMessage content={msg.content} onOpen={onWorkspaceFileOpen} />;

    case "workspace_file_link":
      return <WorkspaceFileLink msg={msg} onOpen={onWorkspaceFileOpen} />;

    case "tool_result":
      return <CCToolResult msg={msg} onOpen={onWorkspaceFileOpen} />;


    case "tool_use":
      return (
        <CCToolCallCard
          toolName={msg.toolName}
          toolInput={msg.toolInput}
          elapsed_ms={msg.elapsed_ms}
          onOpenFile={onWorkspaceFileOpen}
        />
      );

    case "terminal":
      return <CCTerminalBlock command={msg.command} content={msg.content} />;

    case "result": {
      const isError = msg.is_error;
      const costStr = msg.cost_usd != null ? `$${msg.cost_usd.toFixed(4)}` : null;
      const durStr = msg.duration_ms != null ? `${(msg.duration_ms / 1000).toFixed(1)}s` : null;
      return (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 10px",
          borderRadius: "6px",
          background: isError ? "#2d1b1b" : "#1b2d1b",
          border: `1px solid ${isError ? "#5a2020" : "#205a20"}`,
          fontSize: "12px",
          color: isError ? "#f85149" : "#3fb950",
          margin: "6px 0",
        }}>
          <span>{isError ? "❌" : "✅"}</span>
          <span>{isError ? (msg.error_msg || "任务失败") : "任务完成"}</span>
          {costStr && <span style={{ color: "#8b949e", marginLeft: "auto" }}>{costStr}</span>}
          {durStr && <span style={{ color: "#8b949e" }}>{durStr}</span>}
        </div>
      );
    }

    case "system_note":
      return (
        <div style={{
          fontSize: "11px",
          color: "#6e7681",
          padding: "3px 8px",
          borderLeft: "2px solid #484f58",
          margin: "2px 0",
          fontStyle: "italic",
        }}>
          {msg.content}
        </div>
      );

    case "summary":
      return (
        <div style={{
          fontSize: "12px",
          color: "#8b949e",
          padding: "4px 8px",
          borderLeft: "2px solid #30363d",
          margin: "4px 0",
        }}>
          {msg.content}
        </div>
      );

    case "suggestion":
      return (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", margin: "6px 0" }}>
          {(msg.suggestions || []).map((s, i) => (
            <span key={i} style={{
              background: "#161b22",
              border: "1px solid #30363d",
              borderRadius: "14px",
              padding: "4px 12px",
              fontSize: "12px",
              color: "#58a6ff",
              cursor: "pointer",
            }}>
              💡 {typeof s === "string" ? s : s.text || s.label || JSON.stringify(s)}
            </span>
          ))}
        </div>
      );

    default:
      return null;
  }
};

/**
 * 完整思考块（assistant/thinking），折叠时单行显示
 */
const CCThinkingBlock = ({ content }) => {
  const [expanded, setExpanded] = useStateLocal(false);
  if (!content) return null;

  const lines = content.split("\n").filter((l) => l.trim() !== "");
  const totalLines = lines.length;
  // 把所有行用空格拼成一行作为预览
  const inlinePreview = lines.join("  ");

  return (
    <div
      onClick={() => setExpanded((v) => !v)}
      style={{
        margin: "2px 0",
        padding: expanded ? "6px 10px" : "3px 10px",
        background: "#161822",
        border: "1px solid #2a2d3a",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "12px",
        userSelect: "none",
      }}
    >
      {expanded ? (
        <>
          {/* 展开：标题行 */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#8b8fa3", marginBottom: "4px" }}>
            <span>💭</span>
            <span style={{ fontStyle: "italic", fontWeight: 500 }}>思考过程</span>
            <span style={{ marginLeft: "auto", fontSize: "10px", color: "#6e7681" }}>▲ 收起</span>
          </div>
          <pre style={{
            margin: 0,
            padding: "4px 6px",
            background: "#0d0f1a",
            borderRadius: "4px",
            fontSize: "12px",
            color: "#a0a4b8",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "inherit",
            lineHeight: 1.6,
            overflowY: "auto",
            maxHeight: "400px",
          }}>
            {content}
          </pre>
        </>
      ) : (
        /* 折叠：单行，图标 + 内容截断 + 行数提示 */
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          color: "#8b8fa3",
          overflow: "hidden",
          whiteSpace: "nowrap",
          lineHeight: "1.4",
        }}>
          <span style={{ flexShrink: 0 }}>💭</span>
          <span style={{
            fontStyle: "italic",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
            minWidth: 0,
          }}>
            {inlinePreview}
          </span>
          {totalLines > 1 && (
            <span style={{
              flexShrink: 0,
              fontSize: "11px",
              color: "#58a6ff",
              whiteSpace: "nowrap",
            }}>
              还有 {totalLines - 1} 行 ▼
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 流式思考文本（thinking_delta 累积期间显示）
 */
export const CCStreamingThinking = ({ text }) => {
  if (!text) return null;
  const display = text.length > 80 ? "…" + text.slice(-80) : text;
  return (
    <div style={{
      padding: "4px 10px",
      fontSize: "12px",
      color: "#8b8fa3",
      fontStyle: "italic",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      overflow: "hidden",
      minWidth: 0,
    }}>
      <span style={{ flexShrink: 0 }}>💭</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{display}</span>
      <span style={{
        flexShrink: 0,
        display: "inline-block",
        width: "2px",
        height: "12px",
        background: "#8b8fa3",
        animation: "cc-blink 1s step-end infinite",
      }} />
    </div>
  );
};

/**
 * 流式打字机文本块（stream_event 累积期间显示）
 */
export const CCStreamingText = ({ text }) => {
  if (!text) return null;
  return (
    <div style={{ padding: "4px 0", color: "#e6edf3", fontSize: "14px", lineHeight: "1.6", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {text}
      <span style={{
        display: "inline-block",
        width: "2px",
        height: "14px",
        background: "#58a6ff",
        marginLeft: "2px",
        animation: "cc-blink 1s step-end infinite",
        verticalAlign: "text-bottom",
      }} />
      <style>{`@keyframes cc-blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
    </div>
  );
};

export default CCMessageItem;
